import G6, { IG6GraphEvent } from '@antv/g6';
import delay from 'lodash/delay';
import get from 'lodash/get';
import { useCallback, useEffect, useMemo, useRef } from 'react';

import noop from 'utils/noop';
import { DEFAULT_MAX_ZOOM, DEFAULT_MIN_ZOOM, DEFAULT_PADDING, GraphMode } from '../../constants';
import { UseFlowChartIntegrator } from '../../hooks';
import { fixOverlayDimensions, setOverlayVisibility, COMBO_TYPES } from '../../shapes';
import { Edge, Node } from '../../types';
import { goToNode } from '../../utils';
import {
  FLOWCHART_TEST_ID,
  GRID_IMG,
  defaultComboConfigs,
  defaultEdgeConfigs,
  defaultNodeConfigs,
} from './FlowChart.constants';
import { FlowchartWrapper, G6GraphStyled, GGEditorStyled } from './FlowChart.styles';
import { FlowChartProps } from './FlowChart.types';
import { parseAddedItems, parseDeletedItems, parsePastedItems } from './FlowChartHelpers';
import { FlowChartListeners } from './components';

/** Register custom nodes and edges. */
defaultNodeConfigs.forEach(({ name, config, extend }) => G6.registerNode(name, config, extend));
defaultEdgeConfigs.forEach(({ name, config, extend }) => G6.registerEdge(name, config, extend));
defaultComboConfigs.forEach(({ name, config, extend }) => G6.registerCombo(name, config, extend));

export const FlowChart = ({
  nodes = [],
  edges = [],
  combos = [],
  graphOptions = {},
  height = 500,
  width = 1000,
  focusedNode,
  // bools
  isJourneyAssistActive = false,
  isOverlayVisible = false,
  isReadOnly = false,
  // callbacks
  onAdd = noop,
  onAfterRelayout = noop,
  onBeforeRelayout = noop,
  onClone = noop,
  onButtonClick = noop,
  onDelete = noop,
  onLoad = noop,
  onNodeDblClick = noop,
  onItemMouseDown = noop,
  onNodePositionChange = noop,
  onNodeSelectChange = noop,
  onTestInit = noop,
  // components
  children,
  ...rest
}: FlowChartProps) => {
  const flow = useRef(null);

  const handleFlowRefUpdate = useCallback((component: any) => {
    if (component) {
      flow.current = component;

      if (focusedNode) {
        delay(() => goToNode(component.graph, focusedNode), 500);
      }

      /*
       * ! We should not expose this outside of testing so that it is
       * easy to replace g6 if needed.
       * The only place there should be references to either lib is
       * inside ui/workflows/flowchart
       */
      /* istanbul ignore next */
      if (process.env.NODE_ENV === 'test') onTestInit(component);

      onLoad(component.graph);
    }
  }, []);

  const handleCommandExecution = useCallback(
    (e: any) => {
      switch (get(e, 'name')) {
        case 'add':
          onAdd(parseAddedItems(e));
          break;
        case 'paste':
          onClone(parsePastedItems(e));
          break;
        case 'remove':
          onDelete(parseDeletedItems(e));
          break;
        default:
          break;
      }
    },
    [onAdd, onClone, onDelete],
  );

  const getGraph = useCallback(() => get(flow, 'current.graph'), [flow]);

  useEffect(() => {
    const graph = getGraph();

    if (graph) {
      graph.changeSize(width, height);
    }

    if (isOverlayVisible) {
      fixOverlayDimensions(graph);
    }
  }, [height, width]);

  useEffect(() => {
    const graph = getGraph();

    if (graph) {
      graph.setMode(isReadOnly ? GraphMode.Readonly : GraphMode.Default);
    }
  }, [isReadOnly]);

  useEffect(() => {
    const graph = getGraph();

    if (graph) {
      graph.setMode(isJourneyAssistActive ? GraphMode.JourneyAssistActive : GraphMode.Default);
    }
  }, [isJourneyAssistActive]);

  useEffect(() => {
    const graph = getGraph();
    if (graph) {
      setOverlayVisibility(
        graph,
        isOverlayVisible,
        isReadOnly ? GraphMode.Readonly : GraphMode.Default,
      );
    }
  }, [isOverlayVisible]);

  const graphConfig = useMemo(
    () =>
      // options: https://g6.antv.vision/en/docs/api/Graph
      ({
        minZoom: DEFAULT_MIN_ZOOM,
        maxZoom: DEFAULT_MAX_ZOOM,
        height,
        width,
        // Is this a directed graph?  IE: Do the edges only go in one direction?
        directed: true,
        // allows for edges to be in front of nodes
        groupByTypes: false,
        // disables undo/redo
        enabledStack: false,
        // padding between canvas and the border of view port
        fitViewPadding: DEFAULT_PADDING,
        // Available plugins: https://g6.antv.vision/en/docs/api/Plugins
        plugins: [
          // zoomable grid lines
          new G6.Grid({ img: GRID_IMG }),
        ],
        defaultCombo: {
          type: COMBO_TYPES.DEFAULT,
        },
        ...graphOptions,
      }),
    [height, width],
  );

  const data = useMemo(
    () => ({
      nodes,
      edges,
      combos,
    }),
    [nodes, edges, combos],
  );

  /* Callbacks -------------------------------------------------------------- */

  const handleNodeMouseDown = useCallback((e: IG6GraphEvent) => {
    // Make sure it is above other nodes when dragging
    // NOTE: doctype fails with js optional args, so using `lodash/get`
    const item = get(e, 'item');
    if (item) {
      // Make sure the selected item and any attached edges are in front
      item.toFront();
      (item as Node).getEdges().forEach((edge: Edge) => {
        if (edge && edge.toFront) {
          edge.toFront();
        }
      });
    }
  }, []);

  return (
    <FlowchartWrapper data-test={FLOWCHART_TEST_ID} height={height} width={width} {...rest}>
      <GGEditorStyled onAfterExecuteCommand={handleCommandExecution}>
        <FlowChartListeners
          nodes={nodes}
          onAdd={onAdd}
          onAfterRelayout={onAfterRelayout}
          onBeforeRelayout={onBeforeRelayout}
          onButtonClick={onButtonClick}
          onDelete={onDelete}
          onItemMouseDown={onItemMouseDown}
          onNodePositionChange={onNodePositionChange}
          onNodeSelectChange={onNodeSelectChange}
          onNodeDblClick={onNodeDblClick}
        />
        <G6GraphStyled
          onNodeMouseDown={handleNodeMouseDown}
          className="chart-wrapper"
          ref={handleFlowRefUpdate}
          data={data}
          graphConfig={graphConfig}
        >
          <UseFlowChartIntegrator>{children}</UseFlowChartIntegrator>
        </G6GraphStyled>
      </GGEditorStyled>
    </FlowchartWrapper>
  );
};

export default FlowChart;
